import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireAdmin, RequireAuth, RequireNotary } from './auth/guards'
import { RouteLoading } from './components/ui/states'
import { WizardShell } from './wizard/WizardShell'

// Route-level code splitting. Each boundary is a real loading state with a
// `role="status"`, not a blank screen.
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const CallbackPage = lazy(() => import('./pages/CallbackPage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage'))
const ErrorPage = lazy(() => import('./pages/ErrorPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

const HomePage = lazy(() => import('./pages/HomePage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const MyWillsPage = lazy(() => import('./pages/MyWillsPage'))
const WillDetailPage = lazy(() => import('./pages/WillDetailPage'))

const PersonalStep = lazy(() => import('./pages/wizard/PersonalStep'))
const RepresentativesStep = lazy(() => import('./pages/wizard/RepresentativesStep'))
const ReviewStep = lazy(() => import('./pages/wizard/ReviewStep'))
const SubmittedStep = lazy(() => import('./pages/wizard/SubmittedStep'))

const NotaryClaimPage = lazy(() => import('./pages/notary/NotaryClaimPage'))
const NotaryJobsPage = lazy(() => import('./pages/notary/NotaryJobsPage'))
const NotaryJobDetailPage = lazy(() => import('./pages/notary/NotaryJobDetailPage'))

const AdminJobsPage = lazy(() => import('./pages/admin/AdminJobsPage'))
const AdminJobDetailPage = lazy(() => import('./pages/admin/AdminJobDetailPage'))
const AdminNotariesPage = lazy(() => import('./pages/admin/AdminNotariesPage'))
const AdminNotaryFormPage = lazy(() => import('./pages/admin/AdminNotaryFormPage'))
const AdminTemplatesPage = lazy(() => import('./pages/admin/AdminTemplatesPage'))
const AdminTemplateEditorPage = lazy(() => import('./pages/admin/AdminTemplateEditorPage'))
const AdminInterestPage = lazy(() => import('./pages/admin/AdminInterestPage'))

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        {/* --- public ---------------------------------------------------- */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="/error" element={<ErrorPage />} />

        {/* --- notary portal ---------------------------------------------
            A sibling of RequireAuth, never a child of it. RequireAuth calls
            `GET /me`, which provisions a citizen `users` row for whoever asks;
            a notary signing in must not get one. See auth/guards.tsx. */}
        <Route element={<RequireNotary />}>
          <Route path="/notary" element={<NotaryClaimPage />} />
          <Route path="/notary/jobs" element={<NotaryJobsPage />} />
          <Route path="/notary/jobs/:id" element={<NotaryJobDetailPage />} />
        </Route>

        {/* --- signed in ------------------------------------------------- */}
        <Route element={<RequireAuth />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/my-living-wills" element={<MyWillsPage />} />
          <Route path="/my-living-wills/:id" element={<WillDetailPage />} />

          {/* Every wizard step is a route: back/forward and deep links work. */}
          <Route path="/wizard" element={<WizardShell />}>
            <Route path="personal" element={<PersonalStep />} />
            <Route path="representatives" element={<RepresentativesStep />} />
            <Route path="review" element={<ReviewStep />} />
            <Route path="done" element={<SubmittedStep />} />
          </Route>
          {/* DESIGN.md §3 names this route /wizard/submitted; the brief names
              it /wizard/done. Keep both working, one canonical. */}
          <Route path="/wizard/submitted" element={<Navigate to="/wizard/done" replace />} />

          {/* --- admin --------------------------------------------------- */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<Navigate to="/admin/jobs" replace />} />
            <Route path="/admin/jobs" element={<AdminJobsPage />} />
            <Route path="/admin/jobs/:id" element={<AdminJobDetailPage />} />
            <Route path="/admin/notaries" element={<AdminNotariesPage />} />
            <Route path="/admin/notaries/new" element={<AdminNotaryFormPage />} />
            <Route path="/admin/notaries/:id" element={<AdminNotaryFormPage />} />
            <Route path="/admin/templates" element={<AdminTemplatesPage />} />
            {/* `new` before `:id`, or the editor would try to load a template
                whose id is the literal string "new". */}
            <Route path="/admin/templates/new" element={<AdminTemplateEditorPage />} />
            <Route path="/admin/templates/:id" element={<AdminTemplateEditorPage />} />
            <Route path="/admin/interest" element={<AdminInterestPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
